/**
 * Registro de ANCLAS de las Guías in-app: la única lista de «a qué elemento de la pantalla puede
 * apuntar una guía».
 *
 * Una guía (se edita en el Command Center, `/guias`) guarda el `id` del ancla, no un selector CSS:
 * los selectores se rompen con cualquier refactor de clases, y un id que vive en el código se
 * rompe sólo si alguien lo borra — y ahí el test del registro lo avisa.
 *
 * Cómo marcar un elemento nuevo:
 *   1. Sumarlo acá: clave en snake_case, `id` en kebab-case (va adentro de un selector de atributo),
 *      `screen` = el `handle.screen` de la ruta donde se ve ('*' si está en el marco, en todas),
 *      `ui` = 'v1' | 'v2' | 'both', y un `label` que el editor del CC muestra tal cual.
 *   2. En el JSX: `<button {...anchorAttr('mi_clave')}>`. NUNCA `data-guide="…"` a mano: el test
 *      `anchors.registry.test.js` lo rechaza.
 *   3. Correr `node scripts/export-guide-anchors.js` (lo corre solo el `prebuild`) para que el CC
 *      vea el ancla nueva en su selector.
 *
 * Si el elemento se repite (una fila de un listado), se marca SÓLO el primero: la guía apunta a
 * `[data-guide="…"]` y el primero que encuentra el DOM tiene que ser siempre el mismo.
 */
export const GUIDE_ANCHORS = {
    sidebar_clients: {
        id: 'sidebar-clients', screen: '*', ui: 'v2',
        label: 'Barra lateral 2.1 → Clientes',
    },
    sidebar_dashboard: {
        id: 'sidebar-dashboard', screen: '*', ui: 'v2',
        label: 'Barra lateral 2.1 → Tablero',
    },
    sidebar_ia_booster: {
        id: 'sidebar-ia-booster', screen: '*', ui: 'v2',
        label: 'Barra lateral 2.1 → AI Booster',
    },
    // Existe sólo en las agencias con el Centro de Alertas (`_alert_rules_enabled`).
    sidebar_alerts: {
        id: 'sidebar-alerts', screen: '*', ui: 'v2',
        label: 'Barra lateral 2.1 → Alertas (sólo con el Centro de Alertas)',
    },
    sidebar_dashboard_classic: {
        id: 'sidebar-dashboard-classic', screen: '*', ui: 'v1',
        label: 'Barra lateral clásica → Tablero',
    },
    // «Lo nuevo de Dashbo» (card 4SnAMTYV): el aviso de un paso que dice dónde quedó el video de la
    // bienvenida, para quien ya cerró el modal. La entrada existe sólo para quien tiene la guía de
    // bienvenida (`WelcomeVideoNavEntry`): la guía que la use tiene que apuntar a la MISMA audiencia.
    sidebar_welcome_video: {
        id: 'sidebar-welcome-video', screen: '*', ui: 'v2',
        label: 'Barra lateral 2.1 → «Lo nuevo de Dashbo» (reabre el video de bienvenida)',
    },

    // Recorridos de primera visita (tanda 2026-10-02-recorrido-ux21). El Tablero también se abre por
    // `/` (`dashboard`) y el Panel por `/client/:id` (`client_default`): para las guías son la misma
    // pantalla (`screens.js` → `sameGuideScreen`), así que cada ancla se registra en UNA de las dos.
    clients_list_groups: {
        id: 'clients-list-groups', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Primer grupo de la lista («Requieren atención», «En ritmo»…)',
    },
    clients_list_budget: {
        id: 'clients-list-budget', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Columna «Presupuesto / Inversión»',
    },
    clients_list_status: {
        id: 'clients-list-status', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Columna «Estado»',
    },
    clients_list_new_client: {
        id: 'clients-list-new-client', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Botón «Nuevo cliente»',
    },
    // Las celdas de UNA fila: la primera visible con líneas (`guideRow` de `ClientV2Row`). Sólo en la
    // vista Resumen; una agencia sin clientes con líneas no las tiene (pasos opcionales salvo el 1.º).
    clients_row_spend: {
        id: 'clients-row-spend', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Primera fila con líneas: presupuesto e inversión',
    },
    clients_row_goals: {
        id: 'clients-row-goals', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Primera fila con líneas: objetivos',
    },
    clients_row_accounts: {
        id: 'clients-row-accounts', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Primera fila con líneas: cuentas conectadas',
    },
    clients_row_activity: {
        id: 'clients-row-activity', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Primera fila con líneas: actividad',
    },
    clients_row_status: {
        id: 'clients-row-status', screen: 'agency_clients', ui: 'v2',
        label: 'Clientes 2.1 → Primera fila con líneas: estado (alertas)',
    },
    dashboard_lines_column: {
        id: 'dashboard-lines-column', screen: 'marketing', ui: 'both',
        label: 'Tablero → Columna de las líneas de seguimiento (con el +)',
    },
    // La PRIMERA línea de la tabla (`guideRow`, lo decide `Dashboard._renderDashboardRows`). Una
    // agencia sin líneas no las tiene: los pasos que las usan van opcionales.
    dashboard_row_client: {
        id: 'dashboard-row-client', screen: 'marketing', ui: 'both',
        label: 'Tablero → Primera línea: nombre del cliente (abre el cliente)',
    },
    dashboard_row_line: {
        id: 'dashboard-row-line', screen: 'marketing', ui: 'both',
        label: 'Tablero → Primera línea: nombre de la línea (la edita)',
    },
    dashboard_row_expand: {
        id: 'dashboard-row-expand', screen: 'marketing', ui: 'both',
        label: 'Tablero → Primera línea: botón «+» (despliega cuentas y campañas)',
    },
    dashboard_filters: {
        id: 'dashboard-filters', screen: 'marketing', ui: 'both',
        label: 'Tablero → Barra de búsqueda y filtros',
    },
    dashboard_columns: {
        id: 'dashboard-columns', screen: 'marketing', ui: 'both',
        label: 'Tablero → Botón «Columnas»',
    },
    panel_kpis: {
        id: 'panel-kpis', screen: 'client_control_central', ui: 'v2',
        label: 'Panel del cliente → Indicadores del mes',
    },
    panel_lines: {
        id: 'panel-lines', screen: 'client_control_central', ui: 'v2',
        label: 'Panel del cliente → Card «Líneas de seguimiento»',
    },
    panel_data_sources: {
        id: 'panel-data-sources', screen: 'client_control_central', ui: 'v2',
        label: 'Panel del cliente → Card «Fuentes de datos»',
    },
    panel_new_line: {
        id: 'panel-new-line', screen: 'client_control_central', ui: 'v2',
        label: 'Panel del cliente → Botón «Nueva línea de seguimiento»',
    },

    // Guías «Mostrarme cómo» de Aprende Dashbo (tanda 2026-10-03-aprende-dashbo, apr-guias). Cada una
    // arranca en la pantalla de «Hacerlo ahora» de su tarea. Las que viven en un modal o en el cajón de
    // la línea existen sólo con eso abierto: las alcanza un paso anterior que avanza con el clic.
    panel_conversion: {
        id: 'panel-conversion', screen: 'client_control_central', ui: 'v2',
        label: 'Panel del cliente → Chip «Conversión: …» (abre la conversión por cuenta)',
    },
    conversion_edit_events: {
        id: 'conversion-edit-events', screen: 'client_control_central', ui: 'v2',
        label: 'Modal «Conversión que monitorea» → «Editar eventos» de la primera cuenta con eventos',
    },
    conversion_save_events: {
        id: 'conversion-save-events', screen: 'client_control_central', ui: 'v2',
        label: 'Modal «Editar eventos pre-seleccionados» → Botón «Guardar eventos»',
    },
    panel_daily_limit: {
        id: 'panel-daily-limit', screen: 'client_control_central', ui: 'v2',
        label: 'Panel del cliente → Fuentes de datos: lápiz del límite diario de la primera cuenta que lo admite',
    },
    lines_view_budget: {
        id: 'lines-view-budget', screen: 'client_lines', ui: 'v2',
        label: 'Líneas del cliente 2.1 → Presupuesto de la primera línea activa (abre el cajón de edición)',
    },
    line_drawer_budget: {
        id: 'line-drawer-budget', screen: 'client_lines', ui: 'v2',
        label: 'Cajón de edición de la línea → Sección «Presupuesto»',
    },
    line_drawer_save: {
        id: 'line-drawer-save', screen: 'client_lines', ui: 'v2',
        label: 'Cajón de edición de la línea → Botón «Guardar»',
    },
    client_tab_informes: {
        id: 'client-tab-informes', screen: 'informes', ui: 'v2',
        label: 'Ficha del cliente 2.1 → Solapa «Informes» (se dibuja mientras Informes carga)',
    },
    informes_settings: {
        id: 'informes-settings', screen: 'informes', ui: 'both',
        label: 'Informes → Barra «Ajustes» (cuentas, período, comparar)',
    },
    informes_template: {
        id: 'informes-template', screen: 'informes', ui: 'both',
        label: 'Informes → Fila «Plantilla» de la hoja (plantilla elegida y «Métricas y dimensiones»)',
    },
    informes_informe_chip: {
        id: 'informes-informe-chip', screen: 'informes', ui: 'both',
        label: 'Informes → Chip «Informe» (abre un informe guardado)',
    },
    // La lista entera y no la primera fila: el clic en CUALQUIER informe avanza la guía (está en
    // captura sobre la lista). Sin informes guardados no existe: el paso que la usa va opcional.
    informes_picker_list: {
        id: 'informes-picker-list', screen: 'informes', ui: 'both',
        label: 'Informes → Selector de informes: la lista de los guardados',
    },
    informes_save: {
        id: 'informes-save', screen: 'informes', ui: 'both',
        label: 'Informes → Botón «Guardar informe»',
    },
    informes_save_options: {
        id: 'informes-save-options', screen: 'informes', ui: 'both',
        label: 'Informes → Flecha de «Guardar informe» (opciones de guardado)',
    },
    informes_publish: {
        id: 'informes-publish', screen: 'informes', ui: 'both',
        label: 'Informes → Opciones de guardado: «Asociar al portal» / «Quitar del portal»',
    },
    columns_available: {
        id: 'columns-available', screen: 'marketing', ui: 'both',
        label: 'Tablero → Modal de columnas: lista de métricas disponibles',
    },
    columns_save_as: {
        id: 'columns-save-as', screen: 'marketing', ui: 'both',
        label: 'Tablero → Modal de columnas: botón «Guardar como plantilla»',
    },
    booster_feed_guide: {
        id: 'booster-feed-guide', screen: 'client_booster', ui: 'v2',
        label: 'AI Booster → Alimenta: «Ver qué material sirve»',
    },
    booster_feed_omnibox: {
        id: 'booster-feed-omnibox', screen: 'client_booster', ui: 'v2',
        label: 'AI Booster → Alimenta: barra para pegar, escribir o subir material',
    },
    portal_modules: {
        id: 'portal-modules', screen: 'client_portal_config', ui: 'both',
        label: 'Portal del cliente → Módulos (qué ve el cliente)',
    },
    portal_preview: {
        id: 'portal-preview', screen: 'client_portal_config', ui: 'both',
        label: 'Portal del cliente → «Ver portal como cliente»',
    },
    portal_invite: {
        id: 'portal-invite', screen: 'client_portal_config', ui: 'both',
        label: 'Portal del cliente → Botón «Invitar» (arriba)',
    },

    // Usuarios (`/agency-team`), guía «aprende-equipo». Las pone apr-fe-equipo en `AgencyTeam.js`:
    // la lista está en las dos tablas (la clásica y la del avance de la 2.1).
    team_members: {
        id: 'team-members', screen: 'agency_team', ui: 'both',
        label: 'Usuarios → Lista del equipo',
    },
    team_invite: {
        id: 'team-invite', screen: 'agency_team', ui: 'both',
        label: 'Usuarios → Botón «Invitar usuario»',
    },

    // Centro de Alertas: «Ahora» y «Mis reglas» (`/alerts`, `/alerts/rules`) son la pantalla `alerts_center`, y
    // «Inversión sin control» (`/alerts/sin-control`, `alerts_uncontrolled`) se une a ella SÓLO con el Centro (UD-5:
    // `screens.js`); sin el Centro esa solapa se ve sola y no dibuja estas solapas. Las páginas de detalle (UD-18) son
    // `alerts_center_detail`: no tienen solapas. El FE no cambia de solapa por la guía. Las solapas y «Crear una
    // alerta» (sólo quien puede editar) están en las tres vistas de la lista; los números y el «Silenciar», sólo en
    // «Ahora» y con datos (pasos opcionales).
    alerts_kpis: {
        id: 'alerts-kpis', screen: 'alerts_center', ui: 'v2',
        label: 'Alertas → «Ahora»: los cuatro números (Vigentes, Críticas, Aparecieron hoy, Silenciadas)',
    },
    alerts_first_row_snooze: {
        id: 'alerts-first-row-snooze', screen: 'alerts_center', ui: 'v2',
        label: 'Alertas → «Ahora»: «Silenciar» de la primera alerta que se puede silenciar',
    },
    alerts_tab_uncontrolled: {
        id: 'alerts-tab-uncontrolled', screen: 'alerts_center', ui: 'v2',
        label: 'Alertas → Solapa «Inversión sin control»',
    },
    alerts_tab_rules: {
        id: 'alerts-tab-rules', screen: 'alerts_center', ui: 'v2',
        label: 'Alertas → Solapa «Mis reglas»',
    },
    alerts_tab_new: {
        id: 'alerts-tab-new', screen: 'alerts_center', ui: 'v2',
        // Desde UD-18 (24/10) no hay solapa «Crear una alerta»: el ancla es el botón naranja de arriba, en las tres
        // vistas de la lista, como iba la solapa (review C-ud18-fe-07).
        label: 'Alertas → Botón «Crear una alerta» (sólo quien puede editar las alertas)',
    },
    // «Mis reglas», para la guía `aprende-alertas` de Aprende (D7 de la tanda «Aprende y Centro de Alertas», 26/10): la
    // PRIMERA alerta de la lista y su «Editar», en las dos vistas (la convertida de UD-18 y la de antes). «Editar» se marca
    // sólo si quien mira puede editar esa alerta (si no, el botón dice «Ver»): el paso que lo usa va opcional.
    alerts_rules_first_row: {
        id: 'alerts-rules-first-row', screen: 'alerts_center', ui: 'v2',
        label: 'Alertas → «Mis reglas»: la primera alerta de la lista',
    },
    alerts_rules_first_edit: {
        id: 'alerts-rules-first-edit', screen: 'alerts_center', ui: 'v2',
        label: 'Alertas → «Mis reglas»: «Editar» de la primera alerta (sólo quien puede editar)',
    },
};

export const GUIDE_ANCHOR_ATTRIBUTE = 'data-guide';

/**
 * Marca de «esta pantalla todavía carga» para las guías (review C-apr-guias-03). Mientras haya un
 * elemento con este atributo, el runner no empieza a contar la espera de un ancla: la primera carga de
 * Informes tarda más que `TARGET_WAIT_MS` y un paso apretado enseguida se medía como «ancla rota».
 * La pone sólo la pantalla que lo necesita (hoy, los dos loaders de Informes, vía `Loading`), y el
 * runner deja de esperar a los `SCREEN_LOADING_MAX_WAIT_MS` igual.
 */
export const GUIDE_LOADING_ATTRIBUTE = 'data-guide-loading';

export function guideLoadingAttr() {
    return {[GUIDE_LOADING_ATTRIBUTE]: 'true'};
}

export function screenLoadingForGuides() {
    return typeof document !== 'undefined' && !!document.querySelector(`[${GUIDE_LOADING_ATTRIBUTE}]`);
}

/**
 * ¿El evento cayó en la tarjeta, el velo o el punto de una guía? Joyride los dibuja en su propio portal,
 * fuera de cualquier menú de la pantalla. Un menú que se cierra con el clic afuera lo usa para NO
 * cerrarse cuando la persona toca la tarjeta que le está explicando ese menú (review C-apr-guias-02):
 * si no, el ítem iluminado se desmontaba y se medía como «ancla rota».
 */
export function isGuideUi(target) {
    return !!(target && typeof target.closest === 'function'
        && target.closest('#react-joyride-portal, .guide-card'));
}

/** Props para marcar un elemento: `<button {...anchorAttr('sidebar_clients')}>`. */
export function anchorAttr(key) {
    const anchor = GUIDE_ANCHORS[key];
    if (!anchor) {
        // Un typo en la clave no puede romper la pantalla: el elemento queda sin marcar y el test
        // del registro es quien lo reporta.
        return {};
    }
    return {[GUIDE_ANCHOR_ATTRIBUTE]: anchor.id};
}

/** Selector CSS del ancla a partir del `id` que guarda la guía. */
export function anchorSelector(anchorId) {
    return `[${GUIDE_ANCHOR_ATTRIBUTE}="${String(anchorId).replace(/"/g, '')}"]`;
}

/**
 * ¿El elemento del ancla está en la página y se ve? Una celda que la grilla esconde por ancho
 * (`display: none`) está en el DOM pero no se puede iluminar; tampoco un colapsable de alto 0 ni algo
 * con `visibility: hidden` (review C-rec-guias-09): las dos medidas y, donde el navegador lo sabe
 * decir, `checkVisibility()`.
 */
export function anchorDrawable(anchorId) {
    if (!anchorId || typeof document === 'undefined') return false;
    const element = document.querySelector(anchorSelector(anchorId));
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return false;
    return typeof element.checkVisibility !== 'function'
        || element.checkVisibility({visibilityProperty: true, checkVisibilityCSS: true});
}

/**
 * ¿El ancla existe en esta versión del marco? Un ancla de la barra 2.1 no existe en el clásico, y una
 * guía que se ofrece a las dos UIs (el default del editor) no puede esperar 8 s a un elemento que no
 * va a aparecer: eso mediría un «ancla rota» falsa y cerraría la guía sin que nadie la viera
 * (review C-GUIAS-02). Un id que no está en el registro se da por disponible: ahí SÍ es un ancla
 * rota de verdad, y la mide el timeout.
 */
export function anchorAvailableInUi(anchorId, uiVersion) {
    const anchor = Object.values(GUIDE_ANCHORS).find((a) => a.id === anchorId);
    if (!anchor || !uiVersion) return true;
    return anchor.ui === 'both' || anchor.ui === uiVersion;
}

/** La lista que publica `public/guide-anchors.json` para el editor del Command Center. */
export function anchorsForExport() {
    return Object.entries(GUIDE_ANCHORS).map(([key, anchor]) => ({key, ...anchor}));
}
